/**
 * 认证相关API
 */

export interface LoginRequest {
  usernameOrEmail: string;
  password: string;
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  emailCode?: string;
  invitationCode?: string;
  role?: 'user' | 'admin' | 'super_admin';
}

export interface User {
  id: string;
  username: string;
  email: string;
  role: 'user' | 'admin' | 'super_admin';
  status: 'active' | 'inactive' | 'banned';
  created_at: string;
  updated_at: string;
  last_login_at?: string;
  last_login_ip?: string;
  metadata: any;
  // VIP相关字段
  vip_level?: number; // VIP等级: 0-免费, 1-VIP, 2-VVIP, 99-管理员
  vip_expire_at?: string | null; // VIP过期时间
  vip_source?: string | null; // VIP来源
  // OneAPI相关字段
  oneapi_id?: number | null; // OneAPI用户ID
  oneapi_token?: string | null; // OneAPI访问令牌
  quota?: number; // OneAPI配额
  used_quota?: number; // OneAPI已使用配额
  request_count?: number; // OneAPI请求计数
  oneapi_synced_at?: string | null; // OneAPI同步时间
  oneapi_sync_status?: 'synced' | 'pending' | 'failed'; // OneAPI同步状态
  // 邀请码相关字段
  invitation_code?: string | null; // 用户的唯一邀请码
  invited_by?: string | null; // 邀请人用户ID
}

export interface LoginResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  permissionInfo?: any; // VIP权限信息(可选)
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

class AuthAPI {
  private baseURL = '/api';

  /**
   * 用户登录
   */
  async login(data: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    const response = await fetch(`${this.baseURL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || '登录失败');
    }

    return response.json();
  }

  /**
   * 用户注册
   */
  async register(data: RegisterRequest): Promise<ApiResponse<{ user: User }>> {
    const response = await fetch(`${this.baseURL}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || '注册失败');
    }

    return response.json();
  }

  /**
   * 获取当前用户信息
   */
  async getCurrentUser(token: string): Promise<ApiResponse<{ user: User }>> {
    const response = await fetch(`${this.baseURL}/auth/me`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('获取用户信息失败');
    }

    return response.json();
  }

  /**
   * 刷新Token
   */
  async refreshToken(refreshToken: string): Promise<ApiResponse<{ accessToken: string; refreshToken: string }>> {
    const response = await fetch(`${this.baseURL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      throw new Error('刷新Token失败');
    }

    return response.json();
  }

  /**
   * 登出
   */
  async logout(token: string): Promise<ApiResponse> {
    const response = await fetch(`${this.baseURL}/auth/logout`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    return response.json();
  }

  /**
   * 修改密码
   */
  async changePassword(token: string, oldPassword: string, newPassword: string): Promise<ApiResponse> {
    const response = await fetch(`${this.baseURL}/auth/change-password`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ oldPassword, newPassword }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || '修改密码失败');
    }

    return response.json();
  }

  /**
   * 获取邮件服务状态
   */
  async getEmailServiceStatus(): Promise<ApiResponse<{ enabled: boolean; codeExpireSeconds: number }>> {
    const response = await fetch(`${this.baseURL}/auth/email-service-status`);
    return response.json();
  }

  /**
   * 发送注册验证码
   */
  async sendRegisterCode(email: string): Promise<ApiResponse> {
    const response = await fetch(`${this.baseURL}/auth/send-register-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || '发送验证码失败');
    }

    return response.json();
  }

  /**
   * 发送密码重置验证码（无需登录）
   */
  async sendResetCode(email: string): Promise<ApiResponse> {
    const response = await fetch(`${this.baseURL}/auth/send-reset-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || '发送验证码失败');
    }

    return response.json();
  }

  /**
   * 通过邮箱验证码重置密码（无需登录）
   */
  async resetPasswordByEmail(email: string, emailCode: string, newPassword: string): Promise<ApiResponse> {
    const response = await fetch(`${this.baseURL}/auth/reset-password-by-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, emailCode, newPassword }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || '重置密码失败');
    }

    return response.json();
  }

  /**
   * 已登录用户发送修改密码验证码
   */
  async sendChangePasswordCode(token: string): Promise<ApiResponse<{ email: string }>> {
    const response = await fetch(`${this.baseURL}/auth/send-change-password-code`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || '发送验证码失败');
    }

    return response.json();
  }

  /**
   * 已登录用户通过邮箱验证码修改密码
   */
  async changePasswordByEmail(token: string, emailCode: string, newPassword: string): Promise<ApiResponse> {
    const response = await fetch(`${this.baseURL}/auth/change-password-by-email`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ emailCode, newPassword }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || '修改密码失败');
    }

    return response.json();
  }

  // ============ 邀请码相关 ============

  /**
   * 验证邀请码是否有效（无需登录）
   */
  async validateInvitationCode(invitationCode: string): Promise<ApiResponse<{ valid: boolean; inviterUsername?: string }>> {
    const response = await fetch(`${this.baseURL}/auth/validate-invitation-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ invitationCode }),
    });

    return response.json();
  }

  /**
   * 获取当前用户的邀请信息
   */
  async getInvitationInfo(token: string): Promise<ApiResponse<{
    invitationCode: string;
    stats: { total: number; completed: number; totalRewardQuota: number };
    maxInvitations: number;
    rewardQuota: number;
  }>> {
    const response = await fetch(`${this.baseURL}/user/invitation`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || '获取邀请信息失败');
    }

    return response.json();
  }

  /**
   * 获取当前用户的邀请列表
   */
  async getInvitationList(token: string, page = 1, pageSize = 20): Promise<ApiResponse<InvitationRecord[]>> {
    const response = await fetch(`${this.baseURL}/user/invitation/list?page=${page}&pageSize=${pageSize}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || '获取邀请列表失败');
    }

    return response.json();
  }
}

/**
 * 邀请记录
 */
export interface InvitationRecord {
  id: string;
  inviter_id: string;
  invitee_id: string;
  reward_quota: number;
  inviter_rewarded: boolean;
  invitee_rewarded: boolean;
  status: 'pending' | 'completed' | 'failed';
  created_at: string;
  updated_at: string;
  invitee_username?: string;
  invitee_email?: string;
}

export const authAPI = new AuthAPI();

